<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header titleIcon="link"
        title="Integrations"
        subtitle="Manage API keys and webhook endpoints for your agents."
        [tabs]="integrationSurfaceTabs"
        [activeTabKey]="activeTab"
        tablistLabel="Integrations"
        (activeTabKeyChange)="onIntegrationTabKey($event)"
    >
        <ng-container ngProjectAs="[pageHeaderActions]">
            <button
                type="button"
                class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                (click)="openKeyModal()"
            >
                + Generate API Key
            </button>
            <button
                *ngIf="activeTab === 'webhooks'"
                type="button"
                class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                (click)="openWebhookModal()"
            >
                + Register Webhook
            </button>
        </ng-container>
    </app-page-surface-header>

    <app-page-body-panel [tableOnly]="true" [padded]="true">
    <!-- ── API KEYS TAB ──────────────────────────────────────────────────── -->
    <div *ngIf="activeTab === 'api-keys'">
        <p *ngIf="apiKeysLoading" class="text-sm text-gray-400">Loading…</p>
        <p *ngIf="!apiKeysLoading && apiKeysError" class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{{ apiKeysError }}</p>

        <!-- Surfaced above the list rather than left as a status to notice: a key in its grace
             window looks perfectly healthy right up to the moment the window closes. -->
        <div *ngIf="attentionKeys.length"
            class="mb-3 rounded-lg border px-3 py-2 text-sm"
            [ngClass]="overdueCount
                ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300'
                : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300'">
            <button type="button" class="flex w-full items-center justify-between gap-3 text-left" (click)="showAttention = !showAttention">
                <span>
                    <strong>{{ attentionKeys.length }}</strong>
                    {{ attentionKeys.length === 1 ? 'key needs' : 'keys need' }} attention
                    <span *ngIf="overdueCount">— {{ overdueCount }} with a lapsed rotation</span>
                </span>
                <span class="shrink-0 text-xs underline">{{ showAttention ? 'Hide' : 'Show' }}</span>
            </button>

            <ul *ngIf="showAttention" class="mt-2 space-y-1 border-t border-current/20 pt-2">
                <li *ngFor="let k of attentionKeys" class="flex flex-wrap items-center gap-2 text-xs">
                    <code class="rounded bg-black/5 px-1.5 py-0.5 font-mono dark:bg-white/10">{{ k.key_prefix }}…</code>
                    <span class="font-medium">{{ ownerOf(k) }}</span>
                    <span *ngIf="k.label" class="opacity-70">({{ k.label }})</span>
                    <span class="opacity-90">— {{ attentionLabel(k) }}</span>
                </li>
            </ul>
        </div>

        <div class="mb-3 flex flex-wrap items-center gap-2">
            <input
                type="search"
                aria-label="Search by label or prefix"
                placeholder="Search label or prefix"
                class="form-input w-56 shrink-0 rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                [(ngModel)]="keyFilters.q"
                (change)="loadApiKeys()"
            />
            <select aria-label="Filter by client" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-52 shrink-0" [(ngModel)]="keyFilters.tenant_id" (ngModelChange)="loadApiKeys()">
                <option [ngValue]="null">All clients</option>
                <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
            </select>
            <select aria-label="Filter by status" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-44 shrink-0" [(ngModel)]="keyFilters.status" (ngModelChange)="loadApiKeys()">
                <option value="">Any status</option>
                <option value="ACTIVE">Active</option>
                <option value="ROTATING">Rotating</option>
                <option value="REVOKED">Revoked</option>
                <option value="EXPIRED">Expired</option>
            </select>
            <select aria-label="Filter by environment" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-48 shrink-0" [(ngModel)]="keyFilters.environment" (ngModelChange)="loadApiKeys()">
                <option value="">Any environment</option>
                <option value="live">Live</option>
                <option value="test">Test</option>
                <option value="webhook">Webhook</option>
            </select>
            <button *ngIf="keyFiltersActive" type="button" class="text-sm text-primary hover:underline" (click)="clearKeyFilters()">Clear</button>

            <!-- Offered per client, because that is what the endpoint repairs. Tenant services
                 validate against their own copy of these keys; a key that never reached the mirror
                 authenticates nothing, however healthy it looks here. -->
            <button
                *ngIf="keyFilters.tenant_id"
                type="button"
                class="ml-auto rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 dark:border-[#191e3a] dark:hover:bg-white/5"
                [disabled]="syncingTenantId !== null"
                title="Copy this client's keys into the client's own database, where their services check them"
                (click)="syncKeysToTenant(keyFilters.tenant_id!)"
            >
                {{ syncingTenantId === keyFilters.tenant_id ? 'Syncing…' : 'Sync keys to client DB' }}
            </button>
        </div>

        <output *ngIf="syncNotice" class="mb-3 block rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300">
            {{ syncNotice }}
        </output>

        <div *ngIf="!apiKeysLoading && !apiKeysError" class="table-responsive">
            <table class="table table-hover table-surface">
                <thead>
                    <tr>
                        <th>Prefix</th>
                        <th>Client</th>
                        <th>Environment</th>
                        <th>Label</th>
                        <th>Agent</th>
                        <th>
                            <span
                                class="cursor-help decoration-dotted underline-offset-2 [text-decoration-line:underline]"
                                title="Last time this key authenticated against the API (X-API-Key / Bearer). Logging into the tenant portal does not count.">
                                Last Used
                            </span>
                        </th>
                        <th>Expires</th>
                        <th>Status</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let k of apiKeys">
                        <td>
                            <code class="rounded bg-gray-100 px-2 py-0.5 text-xs font-mono dark:bg-white/10">{{ k.key_prefix }}…</code>
                        </td>
                        <td class="text-sm font-medium">{{ ownerLabel(k) }}</td>
                        <td>
                            <!-- A test key reaching production is the mistake this column exists to
                                 prevent, so live and test do not look alike. -->
                            <span
                                class="rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
                                [ngClass]="{
                                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300': k.environment === 'live',
                                    'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300': k.environment === 'test',
                                    'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300': k.environment === 'webhook'
                                }"
                            >{{ k.environment }}</span>
                        </td>
                        <td class="text-sm">{{ k.label || '—' }}</td>
                        <td class="text-sm">{{ k.agent?.agent_code || (k.agent_id ? ('Agent #' + k.agent_id) : 'All agents') }}</td>
                        <td class="text-sm text-gray-500">
                            <span *ngIf="k.last_used_at; else neverUsed">{{ k.last_used_at | date:'dd MMM y' }}</span>
                            <ng-template #neverUsed>
                                <span class="cursor-help" title="This key has not called the API yet. Portal/login access does not update this.">Never</span>
                            </ng-template>
                        </td>
                        <td class="text-sm text-gray-500">{{ k.expires_at ? (k.expires_at | date:'dd MMM y') : 'Never' }}</td>
                        <td>
                            <span
                                class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                                [ngClass]="keyStatusMeta(k).class"
                            >{{ keyStatusMeta(k).label }}</span>
                            <div *ngIf="k.status === 'ROTATING' && k.rotating_until" class="mt-1 text-[11px] text-gray-500">
                                Old key valid until {{ k.rotating_until | date:'dd MMM y, HH:mm' }}
                            </div>
                        </td>
                        <td *ngIf="!canManageKey(k)" class="text-center text-xs text-gray-400">—</td>
                        <td *ngIf="canManageKey(k)" class="text-center">
                            <div class="agent-action-menu relative inline-flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-lg p-1.5 text-gray-600 hover:bg-black/[0.06] dark:text-gray-400 dark:hover:bg-white/10"
                                    (click)="toggleActionMenu('key-' + k.id, $event)"
                                    [attr.aria-expanded]="openActionMenuKey === 'key-' + k.id"
                                    aria-haspopup="menu"
                                    aria-label="Row actions"
                                >
                                    <icon-horizontal-dots class="size-5 rotate-90" />
                                </button>
                                @if (openActionMenuKey === 'key-' + k.id) {
                                <div
                                    appActionMenuPanel
                                    class="min-w-[13rem] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-[#191e3a] dark:bg-[#0e1726]"
                                    role="menu"
                                    (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
                                >
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="flex w-full items-center gap-3 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 dark:text-white-dark dark:hover:bg-white/5 disabled:opacity-40"
                                        (click)="closeActionMenu(); openRotateModal(k)"
                                    >
                                        <icon-refresh class="size-4 shrink-0" />
                                        Rotate key
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 dark:border-[#191e3a] px-3 py-2.5 text-left text-xs font-medium text-danger hover:bg-danger/10 disabled:opacity-40"
                                        [disabled]="revokeLoading[k.id]"
                                        (click)="closeActionMenu(); revokeKey(k.id)"
                                    >
                                        <icon-trash-lines class="size-4 shrink-0" />
                                        Revoke
                                    </button>
                                </div>
                                }
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="apiKeys.length === 0">
                        <td colspan="9" class="py-8 text-center text-sm text-gray-400">
                            {{ keyFiltersActive ? 'No API keys match these filters.' : 'No API keys yet. Generate one to get started.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- ── WEBHOOKS TAB ──────────────────────────────────────────────────── -->
    <div *ngIf="activeTab === 'webhooks'">
        <p *ngIf="webhooksLoading" class="text-sm text-gray-400">Loading…</p>
        <p *ngIf="!webhooksLoading && webhooksError" class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{{ webhooksError }}</p>

        <div *ngIf="testError" class="mb-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{{ testError }}</div>

        <!-- The answer from the client's own server, verbatim. A refusal is the result the operator
             asked for, so it is reported here rather than as an error. -->
        <div *ngIf="testResult"
            class="mb-3 flex items-start justify-between gap-4 rounded-lg border px-3 py-2 text-sm"
            [ngClass]="testResult.success
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300'
                : 'border-red-300 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300'">
            <span>
                <strong>{{ testResult.success ? 'Endpoint accepted' : 'Endpoint refused' }}</strong>
                {{ testResult.event }} —
                {{ testResult.status_code !== null ? 'HTTP ' + testResult.status_code : 'no response' }}
                in {{ testResult.duration_ms }} ms.
                <span *ngIf="testResult.error" class="font-mono text-xs">{{ testResult.error }}</span>
                <span class="block text-xs opacity-80">This was a test — it does not count towards the failure total.</span>
            </span>
            <button type="button" class="shrink-0 opacity-60 hover:opacity-100" (click)="dismissTestResult()">✕</button>
        </div>

        <div class="mb-3 flex flex-wrap items-center gap-2">
            <select aria-label="Filter by client" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-52 shrink-0" [(ngModel)]="webhookFilters.tenant_id" (ngModelChange)="loadWebhooks()">
                <option [ngValue]="null">All clients</option>
                <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
            </select>
            <select aria-label="Filter by state" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-48 shrink-0" [(ngModel)]="webhookFilters.is_active" (ngModelChange)="loadWebhooks()">
                <option value="">Active and paused</option>
                <option value="true">Active only</option>
                <option value="false">Paused only</option>
            </select>
            <!-- Catch-all endpoints (no events subscribed) match every event, so they are listed
                 here too — they do receive the event being asked about. -->
            <select aria-label="Filter by event" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-56 shrink-0" [(ngModel)]="webhookFilters.event" (ngModelChange)="loadWebhooks()">
                <option value="">Any event</option>
                <option *ngFor="let e of supportedEvents" [value]="e.event">{{ e.event }}</option>
            </select>
            <button *ngIf="webhookFiltersActive" type="button" class="text-sm text-primary hover:underline" (click)="clearWebhookFilters()">Clear</button>
        </div>

        <div *ngIf="!webhooksLoading && !webhooksError" class="table-responsive">
            <table class="table table-hover table-surface">
                <thead>
                    <tr>
                        <th>URL</th>
                        <th>Client</th>
                        <th>Label</th>
                        <th>Agent</th>
                        <th>Events</th>
                        <th>Failures</th>
                        <th>Last Triggered</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let w of webhooks">
                        <td>
                            <span class="max-w-[200px] truncate block text-sm font-mono" [title]="w.url">{{ w.url }}</span>
                            <span *ngIf="!w.is_active"
                                class="mt-0.5 inline-block rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                            >Paused</span>
                        </td>
                        <td class="text-sm font-medium">{{ ownerLabel(w) }}</td>
                        <td class="text-sm">{{ w.label || '—' }}</td>
                        <td class="text-sm">{{ w.agent?.agent_code || (w.agent_id ? ('Agent #' + w.agent_id) : 'All agents') }}</td>
                        <td>
                            <div class="flex flex-wrap gap-1">
                                <span *ngIf="w.events.length === 0" class="text-xs text-gray-400">All events</span>
                                <span
                                    *ngFor="let e of w.events"
                                    class="rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-white/10"
                                >{{ e }}</span>
                            </div>
                        </td>
                        <td class="text-sm">
                            <button
                                type="button"
                                class="underline decoration-dotted underline-offset-2 hover:text-primary"
                                [class.text-danger]="w.failure_count > 0"
                                title="Show the delivery attempts behind this count"
                                (click)="openDeliveries(w)"
                            >{{ w.failure_count }}</button>
                        </td>
                        <td class="text-sm text-gray-500">{{ w.last_triggered_at ? (w.last_triggered_at | date:'dd MMM y') : 'Never' }}</td>
                        <td class="text-center">
                            <div class="agent-action-menu relative inline-flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-lg p-1.5 text-gray-600 hover:bg-black/[0.06] dark:text-gray-400 dark:hover:bg-white/10"
                                    (click)="toggleActionMenu('wh-' + w.id, $event)"
                                    [attr.aria-expanded]="openActionMenuKey === 'wh-' + w.id"
                                    aria-haspopup="menu"
                                    aria-label="Row actions"
                                >
                                    <icon-horizontal-dots class="size-5 rotate-90" />
                                </button>
                                @if (openActionMenuKey === 'wh-' + w.id) {
                                <div
                                    appActionMenuPanel
                                    class="min-w-[15rem] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-[#191e3a] dark:bg-[#0e1726]"
                                    role="menu"
                                    (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
                                >
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 disabled:opacity-40 dark:text-white-dark dark:hover:bg-white/5"
                                        [disabled]="testingWebhookId === w.id"
                                        (click)="closeActionMenu(); sendTestEvent(w)">
                                        <icon-send class="size-4 shrink-0" />
                                        Send test event
                                    </button>
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 dark:border-[#191e3a] dark:text-white-dark dark:hover:bg-white/5"
                                        (click)="closeActionMenu(); openDeliveries(w)">
                                        <icon-clock class="size-4 shrink-0" />
                                        Delivery history
                                    </button>
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 dark:border-[#191e3a] dark:text-white-dark dark:hover:bg-white/5"
                                        (click)="closeActionMenu(); openWebhookEditModal(w)">
                                        <icon-edit class="size-4 shrink-0" />
                                        Edit
                                    </button>
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 disabled:opacity-40 dark:border-[#191e3a] dark:text-white-dark dark:hover:bg-white/5"
                                        [disabled]="toggleWebhookLoading[w.id]"
                                        (click)="closeActionMenu(); toggleWebhookActive(w)">
                                        <icon-minus-circle *ngIf="w.is_active" class="size-4 shrink-0" />
                                        <icon-play-circle *ngIf="!w.is_active" class="size-4 shrink-0" />
                                        {{ w.is_active ? 'Pause deliveries' : 'Resume deliveries' }}
                                    </button>
                                    <!-- Rotating and removing both cost the client something, so they sit
                                         below a divider, away from the everyday actions. -->
                                    @if (P.WEBHOOK_ROTATE_SECRET | hasPermission) {
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-amber-700 hover:bg-amber-50 dark:border-[#191e3a] dark:text-amber-300 dark:hover:bg-amber-500/10"
                                        (click)="closeActionMenu(); askRotateSecret(w)">
                                        <icon-refresh class="size-4 shrink-0" />
                                        Rotate signing secret
                                    </button>
                                    }
                                    <button type="button" role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-medium text-danger hover:bg-danger/10 disabled:opacity-40 dark:border-[#191e3a]"
                                        [disabled]="deleteWebhookLoading[w.id]"
                                        (click)="closeActionMenu(); deleteWebhook(w.id)">
                                        <icon-trash-lines class="size-4 shrink-0" />
                                        Remove webhook
                                    </button>
                                </div>
                                }
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="webhooks.length === 0">
                        <td colspan="8" class="py-8 text-center text-sm text-gray-400">
                            {{ webhookFiltersActive ? 'No webhooks match these filters.' : 'No webhooks registered yet.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- ── DEAD-LETTERS (failed webhook deliveries) ─────────────────────── -->
    <div *ngIf="activeTab === 'dead-letters'">
        <div class="mb-3 flex items-center justify-between">
            <p class="text-sm text-gray-500">
                Deliveries that exhausted all retry attempts. Replay re-queues for another full set of attempts; discard drops it permanently.
            </p>
            <button
                type="button"
                class="rounded-lg border border-gray-200 px-3 py-1 text-xs hover:bg-gray-50 dark:border-[#191e3a] dark:hover:bg-white/5"
                (click)="loadDeadLetters()"
            >Refresh</button>
        </div>

        <div class="mb-3 flex flex-wrap items-center gap-2">
            <select aria-label="Filter by client" class="form-select rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726] w-52 shrink-0" [(ngModel)]="deadLetterClientId" (ngModelChange)="loadDeadLetters()">
                <option [ngValue]="null">All clients</option>
                <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
            </select>
            <button *ngIf="deadLetterClientId" type="button" class="text-sm text-primary hover:underline" (click)="clearDeadLetterFilters()">Clear</button>
        </div>

        <!-- Said out loud rather than inferred from a short list: an incomplete answer that looks
             complete is worse than no answer. -->
        <p *ngIf="deadLettersTruncated"
            class="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
            The failed queue is larger than this view can scan, so older failures for this client may not be listed.
        </p>

        <p *ngIf="deadLettersLoading" class="text-sm text-gray-400">Loading…</p>
        <p *ngIf="!deadLettersLoading && deadLettersError" class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{{ deadLettersError }}</p>

        <div *ngIf="!deadLettersLoading && !deadLettersError" class="table-responsive">
            <table class="table table-hover table-surface">
                <thead>
                    <tr>
                        <th>Event</th>
                        <th>URL</th>
                        <th>Attempts</th>
                        <th>Last Error</th>
                        <th>Failed</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let d of deadLetters">
                        <td class="text-sm font-medium">{{ d.event }}</td>
                        <td>
                            <span class="block max-w-[200px] truncate text-sm font-mono" [title]="d.url">{{ d.url }}</span>
                            <span class="text-xs text-gray-400">webhook #{{ d.webhook_id }} · {{ d.delivery_id }}</span>
                        </td>
                        <td class="text-sm">{{ d.attempts_made }}</td>
                        <td>
                            <span class="block max-w-[220px] truncate text-xs text-danger" [title]="d.failed_reason || ''">{{ d.failed_reason || '—' }}</span>
                        </td>
                        <td class="text-sm text-gray-500">{{ d.failed_at ? (d.failed_at | date:'dd MMM y, HH:mm') : '—' }}</td>
                        <td class="text-center">
                            <div class="agent-action-menu relative inline-flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-lg p-1.5 text-gray-600 hover:bg-black/[0.06] dark:text-gray-400 dark:hover:bg-white/10"
                                    (click)="toggleActionMenu('dl-' + d.job_id, $event)"
                                    [attr.aria-expanded]="openActionMenuKey === 'dl-' + d.job_id"
                                    aria-haspopup="menu"
                                    aria-label="Row actions"
                                >
                                    <icon-horizontal-dots class="size-5 rotate-90" />
                                </button>
                                @if (openActionMenuKey === 'dl-' + d.job_id) {
                                <div
                                    appActionMenuPanel
                                    class="min-w-[13rem] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-[#191e3a] dark:bg-[#0e1726]"
                                    role="menu"
                                    (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
                                >
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="flex w-full items-center gap-3 px-3 py-2.5 text-left text-xs font-medium text-[#18214d] hover:bg-gray-50 dark:text-white-dark dark:hover:bg-white/5 disabled:opacity-40"
                                        [disabled]="replayLoading[d.job_id]"
                                        (click)="closeActionMenu(); replayDeadLetter(d.job_id)"
                                    >
                                        <icon-restore class="size-4 shrink-0" />
                                        Replay delivery
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="flex w-full items-center gap-3 border-t border-gray-100 dark:border-[#191e3a] px-3 py-2.5 text-left text-xs font-medium text-danger hover:bg-danger/10 disabled:opacity-40"
                                        [disabled]="discardLoading[d.job_id]"
                                        (click)="closeActionMenu(); discardDeadLetter(d.job_id)"
                                    >
                                        <icon-trash-lines class="size-4 shrink-0" />
                                        Discard
                                    </button>
                                </div>
                                }
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="deadLetters.length === 0">
                        <td colspan="6" class="py-8 text-center text-sm text-gray-400">No failed deliveries — all webhooks are healthy.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    </app-page-body-panel>
</div>

<!-- ── GENERATE API KEY MODAL ───────────────────────────────────────────── -->
<dialog
    *ngIf="showKeyModal"
    open
    class="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center overflow-y-auto border-0 bg-black/50 p-4 py-6 sm:py-8 backdrop-blur-[2px]"
    aria-labelledby="generate-api-key-title"
    (click)="closeKeyModal()"
    (keydown.escape)="closeKeyModal()"
>
    <div
        class="my-auto flex w-full max-w-md max-h-[min(90dvh,calc(100dvh-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-[#0e1726]"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
    >

        <!-- After generation: show the key once -->
        <ng-container *ngIf="newlyGeneratedKey; else keyFormTpl">
            <div class="p-6">
                <h2 class="mb-1 text-base font-bold text-black dark:text-white">API Key Generated</h2>
                <p class="mb-4 text-sm text-amber-600 dark:text-amber-400">Copy your key now — it will <strong>never</strong> be shown again.</p>
                <div class="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-[#191e3a] dark:bg-white/5">
                    <code class="flex-1 break-all text-xs font-mono text-black dark:text-white">{{ newlyGeneratedKey.key }}</code>
                    <button type="button" class="shrink-0 text-xs font-medium text-primary underline" (click)="copyKey()">
                        {{ keyCopied ? 'Copied!' : 'Copy' }}
                    </button>
                </div>
                <div class="flex justify-end">
                    <button type="button" class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white" (click)="closeKeyModal()">Done</button>
                </div>
            </div>
        </ng-container>

        <ng-template #keyFormTpl>
            <div class="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-[#191e3a]">
                <h2 id="generate-api-key-title" class="text-base font-bold text-black dark:text-white">Generate API Key</h2>
                <button type="button" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" (click)="closeKeyModal()" aria-label="Close">✕</button>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            <div class="space-y-4">
                <div>
                    <label for="key-form-client" class="mb-1 block text-sm font-medium">Client <span class="text-red-500">*</span></label>
                    <select id="key-form-client" class="form-select w-full rounded-xl" [(ngModel)]="keyForm.tenant_id" (ngModelChange)="onKeyClientChange()">
                        <option [ngValue]="null">— Select client —</option>
                        <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
                    </select>
                </div>
                <div>
                    <label for="key-form-agent" class="mb-1 block text-sm font-medium">Agent</label>
                    <select
                        id="key-form-agent"
                        class="form-select w-full rounded-xl disabled:opacity-50"
                        [(ngModel)]="keyForm.agent_id"
                        [disabled]="!keyForm.tenant_id || keyAgentsLoading"
                    >
                        <option [ngValue]="null">All agents (tenant-wide)</option>
                        <option *ngFor="let a of keyAgents" [ngValue]="a.id">{{ agentLabel(a) }}</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-400">
                        <ng-container *ngIf="!keyForm.tenant_id">Select a client first.</ng-container>
                        <ng-container *ngIf="keyForm.tenant_id && keyAgentsLoading">Loading agents…</ng-container>
                        <ng-container *ngIf="keyForm.tenant_id && !keyAgentsLoading && keyAgents.length === 0">No published agents for this client — the key will be tenant-wide.</ng-container>
                        <ng-container *ngIf="keyForm.tenant_id && !keyAgentsLoading && keyAgents.length > 0">Leave as “All agents” for a tenant-wide key, or pick one agent to scope the key.</ng-container>
                    </p>
                </div>
                <div>
                    <label for="key-form-label" class="mb-1 block text-sm font-medium">Label</label>
                    <input id="key-form-label" type="text" class="form-input w-full rounded-xl" [(ngModel)]="keyForm.label" placeholder="Production key" />
                </div>
                <div>
                    <label for="key-form-expires" class="mb-1 block text-sm font-medium">Expires At (optional)</label>
                    <input id="key-form-expires" type="date" class="form-input w-full rounded-xl" [(ngModel)]="keyForm.expires_at" />
                </div>
                <div>
                    <label for="key-form-environment" class="mb-1 block text-sm font-medium">Environment</label>
                    <select id="key-form-environment" class="form-select w-full rounded-xl" [(ngModel)]="keyForm.environment">
                        <option value="live">Live (production)</option>
                        <option value="test">Test (sandbox)</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-400">Test keys are blocked from production-only endpoints.</p>
                </div>
                <div>
                    <fieldset>
                    <legend class="mb-1 block text-sm font-medium">Scopes</legend>
                    <div class="flex flex-wrap gap-2">
                        <button
                            *ngFor="let s of availableScopes"
                            type="button"
                            [title]="s.description"
                            (click)="toggleScope(s.value)"
                            class="rounded-full border px-3 py-1 text-xs transition"
                            [ngClass]="isScopeSelected(s.value)
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-[#191e3a] dark:text-white-dark dark:hover:bg-white/5'"
                        >
                            {{ s.value }}
                        </button>
                    </div>
                    </fieldset>
                    <!-- custom scopes already selected -->
                    <div *ngIf="customSelectedScopes.length" class="mt-2 flex flex-wrap gap-2">
                        <span
                            *ngFor="let s of customSelectedScopes"
                            class="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs text-primary"
                        >
                            {{ s }}
                            <button type="button" class="leading-none" (click)="toggleScope(s)" aria-label="Remove scope">✕</button>
                        </span>
                    </div>
                    <!-- add a custom scope -->
                    <div class="mt-2 flex gap-2">
                        <input
                            type="text"
                            class="form-input flex-1 rounded-xl"
                            [(ngModel)]="customScope"
                            (keydown.enter)="$event.preventDefault(); addCustomScope()"
                            placeholder="Add custom scope, e.g. invoice:read"
                        />
                        <button type="button" class="rounded-xl border px-4 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="addCustomScope()">Add</button>
                    </div>
                    <p class="mt-1 text-xs text-gray-400">Pick from the catalog or add your own. Leave empty for no scope restriction.</p>
                </div>
                <div>
                    <label for="key-form-ip-whitelist" class="mb-1 block text-sm font-medium">IP Whitelist (CIDR)</label>
                    <textarea id="key-form-ip-whitelist" rows="2" class="form-textarea w-full rounded-xl" [(ngModel)]="keyForm.ip_whitelist" placeholder="203.0.113.0/24, 198.51.100.7/32"></textarea>
                    <p class="mt-1 text-xs text-gray-400">Comma or newline separated. Leave blank to allow any IP.</p>
                </div>
                <div>
                    <label for="key-form-rate-limit" class="mb-1 block text-sm font-medium">Rate limit (requests/min)</label>
                    <input id="key-form-rate-limit" type="number" min="10" max="1000" class="form-input w-full rounded-xl" [(ngModel)]="keyForm.rate_limit_per_min" placeholder="100" />
                    <p class="mt-1 text-xs text-gray-400">Optional. 10–1000. Defaults to 100 if blank.</p>
                </div>
                <p *ngIf="keyGenError" class="text-xs text-danger">{{ keyGenError }}</p>
            </div>
            </div>

            <div class="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-[#191e3a]">
                <button type="button" class="rounded-full border px-6 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="closeKeyModal()">Cancel</button>
                <button
                    type="button"
                    class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white disabled:opacity-50"
                    [disabled]="keyGenLoading"
                    (click)="generateKey()"
                >
                    {{ keyGenLoading ? 'Generating…' : 'Generate' }}
                </button>
            </div>
        </ng-template>
    </div>
</dialog>

<!-- ── ROTATE API KEY MODAL ─────────────────────────────────────────────── -->
<div *ngIf="showRotateModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-[#0e1726]">

        <!-- After rotation: show the new key once -->
        <ng-container *ngIf="rotatedKey; else rotateFormTpl">
            <h2 class="mb-1 text-base font-bold text-black dark:text-white">API Key Rotated</h2>
            <p class="mb-4 text-sm text-amber-600 dark:text-amber-400">Copy your new key now — it will <strong>never</strong> be shown again.</p>
            <div class="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-[#191e3a] dark:bg-white/5">
                <code class="flex-1 break-all text-xs font-mono text-black dark:text-white">{{ rotatedKey.key }}</code>
                <button type="button" class="shrink-0 text-xs font-medium text-primary underline" (click)="copyRotatedKey()">
                    {{ rotatedKeyCopied ? 'Copied!' : 'Copy' }}
                </button>
            </div>
            <p class="mb-4 text-xs text-gray-500">The old key stays valid until <strong>{{ rotatedKey.old_key_valid_until | date:'dd MMM y, HH:mm' }}</strong>, then it is auto-revoked.</p>
            <div class="flex justify-end">
                <button type="button" class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white" (click)="closeRotateModal()">Done</button>
            </div>
        </ng-container>

        <ng-template #rotateFormTpl>
            <div class="mb-4 flex items-center justify-between">
                <h2 class="text-base font-bold text-black dark:text-white">Rotate API Key</h2>
                <button type="button" class="text-gray-400 hover:text-gray-600" (click)="closeRotateModal()">✕</button>
            </div>

            <p class="mb-4 text-sm text-gray-500">
                A new key will be issued for
                <code class="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono dark:bg-white/10">{{ rotateTarget?.key_prefix }}…</code>
                <span *ngIf="rotateTarget?.label"> ({{ rotateTarget?.label }})</span>.
                The current key keeps working during the grace period below.
            </p>

            <div class="space-y-4">
                <div>
                    <label for="rotate-grace-hours" class="mb-1 block text-sm font-medium">Grace period (hours)</label>
                    <input id="rotate-grace-hours" type="number" min="0" max="168" class="form-input w-full rounded-xl" [(ngModel)]="rotateGraceHours" placeholder="48" />
                    <p class="mt-1 text-xs text-gray-400">How long the old key stays valid (0–168). Set 0 to revoke it immediately.</p>
                </div>
                <p *ngIf="rotateError" class="text-xs text-danger">{{ rotateError }}</p>
            </div>

            <div class="mt-6 flex justify-end gap-3">
                <button type="button" class="rounded-full border px-6 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="closeRotateModal()">Cancel</button>
                <button
                    type="button"
                    class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white disabled:opacity-50"
                    [disabled]="rotateLoading"
                    (click)="rotateKey()"
                >
                    {{ rotateLoading ? 'Rotating…' : 'Rotate' }}
                </button>
            </div>
        </ng-template>
    </div>
</div>

<!-- ── REGISTER WEBHOOK MODAL ────────────────────────────────────────────── -->
<dialog
    *ngIf="showWebhookModal"
    open
    class="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center overflow-y-auto border-0 bg-black/50 p-4 py-6 sm:py-8 backdrop-blur-[2px]"
    [attr.aria-labelledby]="newlyRegisteredWebhook ? 'webhook-registered-title' : 'webhook-form-title'"
    (click)="closeWebhookModal()"
    (keydown.escape)="closeWebhookModal()"
>
    <div
        class="my-auto flex w-full max-w-lg max-h-[min(90dvh,calc(100dvh-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-[#0e1726]"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
    >

        <!-- After registration: show secret once -->
        <ng-container *ngIf="newlyRegisteredWebhook; else webhookFormTpl">
            <div class="p-6">
                <h2 id="webhook-registered-title" class="mb-1 text-base font-bold text-black dark:text-white">Webhook Registered</h2>
                <p class="mb-4 text-sm text-amber-600 dark:text-amber-400">Copy your signing secret now — it will <strong>never</strong> be shown again.</p>
                <div class="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-[#191e3a] dark:bg-white/5">
                    <code class="flex-1 break-all text-xs font-mono text-black dark:text-white">{{ newlyRegisteredWebhook.secret }}</code>
                    <button type="button" class="shrink-0 text-xs font-medium text-primary underline" (click)="copySecret()">
                        {{ secretCopied ? 'Copied!' : 'Copy' }}
                    </button>
                </div>
                <!-- The header name here has to match what webhook-dispatch.processor.ts actually sends.
                     It read X-AgenticPD-Signature, which is not a header the platform has ever sent, so
                     a client coding to this line verified nothing and rejected every delivery. -->
                <p class="mb-4 text-xs text-gray-500">
                    The client verifies the <code>X-Agentic-Signature</code> header with it:
                    <code>sha256=HMAC-SHA256(secret, rawRequestBody)</code>, computed over the raw bytes.
                </p>
                <div class="flex justify-end">
                    <button type="button" class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white" (click)="closeWebhookModal()">Done</button>
                </div>
            </div>
        </ng-container>

        <ng-template #webhookFormTpl>
            <div class="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-[#191e3a]">
                <h2 id="webhook-form-title" class="text-base font-bold text-black dark:text-white">
                    {{ editingWebhookId === null ? 'Register Webhook' : 'Edit Webhook' }}
                </h2>
                <button type="button" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" (click)="closeWebhookModal()" aria-label="Close">✕</button>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
                <p *ngIf="editingWebhookId !== null" class="mb-4 text-sm text-gray-500">
                    The signing secret is unchanged — the client does not need to redeploy.
                </p>

                <div class="space-y-4">
                    <div>
                        <label for="webhook-url" class="mb-1 block text-sm font-medium">HTTPS URL <span class="text-red-500">*</span></label>
                        <input id="webhook-url" type="url" class="form-input w-full rounded-xl" [(ngModel)]="webhookForm.url" placeholder="https://example.com/webhook" />
                    </div>
                    <div>
                        <label for="webhook-tenant-id" class="mb-1 block text-sm font-medium">Client <span class="text-red-500">*</span></label>
                        <!-- Picked by name rather than typed as an id, and fixed once registered:
                             retargeting an endpoint at another tenant would silently redirect one
                             client's events to another. -->
                        <select
                            id="webhook-tenant-id"
                            class="form-select w-full rounded-xl disabled:cursor-not-allowed disabled:opacity-60"
                            [ngModel]="webhookForm.tenant_id"
                            (ngModelChange)="webhookForm.tenant_id = $event; onWebhookClientChange()"
                            [disabled]="editingWebhookId !== null"
                        >
                            <option [ngValue]="null">Select a client…</option>
                            <option *ngFor="let c of clients" [ngValue]="c.id">{{ clientLabel(c) }}</option>
                        </select>
                        <p *ngIf="editingWebhookId !== null" class="mt-1 text-xs text-gray-400">
                            Register a new webhook to send these events to a different client.
                        </p>
                    </div>
                    <div>
                        <label for="webhook-agent-id" class="mb-1 block text-sm font-medium">Agent (optional — leave blank for all agents)</label>
                        <!-- Only the chosen client's agents: a webhook scoped to another client's agent
                             would subscribe to events that agent never raises for this client, so it
                             would simply never fire and nothing would say why. -->
                        <select
                            id="webhook-agent-id"
                            class="form-select w-full rounded-xl disabled:opacity-50"
                            [(ngModel)]="webhookForm.agent_id"
                            [disabled]="!webhookForm.tenant_id || webhookAgentsLoading"
                        >
                            <option [ngValue]="null">All agents</option>
                            <option *ngFor="let a of webhookAgents" [ngValue]="a.id">{{ agentLabel(a) }}</option>
                        </select>
                        <p class="mt-1 text-xs text-gray-400">
                            <ng-container *ngIf="!webhookForm.tenant_id">Select a client first.</ng-container>
                            <ng-container *ngIf="webhookForm.tenant_id && webhookAgentsLoading">Loading agents…</ng-container>
                            <ng-container *ngIf="webhookForm.tenant_id && !webhookAgentsLoading && webhookAgents.length === 0">No published agents for this client — the webhook will cover every agent.</ng-container>
                            <ng-container *ngIf="webhookForm.tenant_id && !webhookAgentsLoading && webhookAgents.length > 0">Leave as “All agents” to receive events from every agent of this client.</ng-container>
                        </p>
                    </div>
                    <div>
                        <label for="webhook-label" class="mb-1 block text-sm font-medium">Label</label>
                        <input id="webhook-label" type="text" class="form-input w-full rounded-xl" [(ngModel)]="webhookForm.label" placeholder="My webhook" />
                    </div>
                    <div>
                        <fieldset>
                        <legend class="mb-2 block text-sm font-medium">Events (leave all unchecked to receive every event)</legend>
                        <div class="max-h-52 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-gray-50/50 p-3 dark:border-[#191e3a] dark:bg-white/5">
                            <div class="flex flex-wrap gap-2">
                                <!-- An event nothing raises yet is offered but marked: a client subscribing to
                                     it would wait forever, and silence is indistinguishable from a broken
                                     endpoint. Still selectable, so a subscription made ahead of the
                                     implementation is not blocked. -->
                                <button
                                    *ngFor="let e of supportedEvents"
                                    type="button"
                                    class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
                                    [ngClass]="isEventSelected(e.event)
                                        ? 'border-[#303030] bg-[#303030] text-white'
                                        : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726]'"
                                    [title]="e.emitted ? '' : 'Not raised by the platform yet — a subscription receives nothing'"
                                    (click)="toggleEvent(e.event)"
                                >
                                    <span>{{ e.event }}</span>
                                    <span *ngIf="!e.emitted"
                                        class="rounded-full px-1.5 py-px text-[10px] font-semibold"
                                        [ngClass]="isEventSelected(e.event) ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'"
                                    >not yet emitted</span>
                                </button>
                            </div>
                        </div>
                        </fieldset>
                    </div>
                    <p *ngIf="webhookSaveError" class="text-xs text-danger">{{ webhookSaveError }}</p>
                </div>
            </div>

            <div class="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-[#191e3a]">
                <button type="button" class="rounded-full border px-6 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="closeWebhookModal()">Cancel</button>
                <button
                    type="button"
                    class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white disabled:opacity-50"
                    [disabled]="webhookSaveLoading"
                    (click)="saveWebhook()"
                >
                    <ng-container *ngIf="editingWebhookId === null; else savingEdit">
                        {{ webhookSaveLoading ? 'Registering…' : 'Register' }}
                    </ng-container>
                    <ng-template #savingEdit>{{ webhookSaveLoading ? 'Saving…' : 'Save changes' }}</ng-template>
                </button>
            </div>
        </ng-template>
    </div>
</dialog>

<!-- ── WEBHOOK DELIVERY HISTORY ──────────────────────────────────────────── -->
<div *ngIf="deliveriesFor" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div class="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-white p-6 shadow-xl dark:bg-[#0e1726]">
        <div class="mb-1 flex items-start justify-between gap-4">
            <div class="min-w-0">
                <h2 class="text-base font-bold text-black dark:text-white">Delivery history</h2>
                <p class="truncate font-mono text-xs text-gray-500" [title]="deliveriesFor.url">{{ deliveriesFor.url }}</p>
            </div>
            <button type="button" class="text-gray-400 hover:text-gray-600" (click)="closeDeliveries()">✕</button>
        </div>
        <!-- Stated rather than left to be inferred: an absent attempt from last year is retention,
             not proof the event was never sent. -->
        <p class="mb-3 text-xs text-gray-400">
            Kept for {{ deliveriesRetentionDays }} days. Each retry is its own row.
        </p>

        <div class="mb-3 flex flex-wrap items-center gap-2">
            <select aria-label="Filter by outcome" class="form-select w-44 shrink-0 rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                [(ngModel)]="deliveryFilters.outcome" (ngModelChange)="onDeliveryFiltersChange()">
                <option value="">Any outcome</option>
                <option value="accepted">Accepted</option>
                <option value="failed">Failed</option>
            </select>
            <select aria-label="Filter by event" class="form-select w-56 shrink-0 rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                [(ngModel)]="deliveryFilters.event" (ngModelChange)="onDeliveryFiltersChange()">
                <option value="">Any event</option>
                <option *ngFor="let e of supportedEvents" [value]="e.event">{{ e.event }}</option>
            </select>
            <select aria-label="Filter by kind" class="form-select w-48 shrink-0 rounded-full border-gray-300 py-2 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
                [(ngModel)]="deliveryFilters.is_test" (ngModelChange)="onDeliveryFiltersChange()">
                <option value="">Real and test</option>
                <option value="false">Real events only</option>
                <option value="true">Test sends only</option>
            </select>
            <button *ngIf="deliveryFiltersActive" type="button" class="text-sm text-primary hover:underline" (click)="clearDeliveryFilters()">Clear</button>
        </div>

        <p *ngIf="deliveriesLoading" class="text-sm text-gray-400">Loading…</p>
        <p *ngIf="!deliveriesLoading && deliveriesError" class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{{ deliveriesError }}</p>

        <div *ngIf="!deliveriesLoading && !deliveriesError" class="min-h-0 flex-1 overflow-y-auto">
            <table class="table table-hover table-surface">
                <thead>
                    <tr>
                        <th>When</th>
                        <th>Event</th>
                        <th>Try</th>
                        <th>Result</th>
                        <th>Took</th>
                        <th>Detail</th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let entry of deliveries">
                    <!-- A change sits in the same order as the attempts: a rotation immediately
                         above a run of failures is the explanation for them. -->
                    <tr *ngIf="entry.kind === 'AUDIT'" class="bg-amber-50/60 dark:bg-amber-500/10">
                        <td class="whitespace-nowrap text-sm text-gray-500">{{ entry.created_at | date:'dd MMM y, HH:mm:ss' }}</td>
                        <td colspan="5" class="text-xs font-medium text-amber-800 dark:text-amber-300">
                            {{ auditLabel($any(entry).action) }}
                        </td>
                    </tr>
                    <tr *ngIf="asDelivery(entry) as d">
                        <td class="whitespace-nowrap text-sm text-gray-500">{{ d.created_at | date:'dd MMM y, HH:mm:ss' }}</td>
                        <td class="text-sm font-mono text-xs">
                            {{ d.event }}
                            <span *ngIf="d.is_test"
                                class="ml-1 rounded-full bg-gray-200 px-1.5 py-px text-[10px] font-sans font-medium text-gray-600 dark:bg-white/10 dark:text-gray-300"
                            >test</span>
                        </td>
                        <td class="text-sm">{{ d.attempt }}</td>
                        <td>
                            <span
                                class="rounded-full px-2 py-0.5 text-xs font-medium"
                                [ngClass]="d.success
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300'"
                            >{{ deliveryOutcome(d) }}</span>
                        </td>
                        <td class="text-sm text-gray-500">{{ d.duration_ms !== null ? d.duration_ms + ' ms' : '—' }}</td>
                        <td class="max-w-[260px] truncate text-xs text-gray-500" [title]="d.error || d.delivery_id">
                            {{ d.error || d.delivery_id }}
                        </td>
                    </tr>
                    </ng-container>
                    <tr *ngIf="deliveries.length === 0 && !deliveriesLoading">
                        <td colspan="6" class="py-8 text-center text-sm text-gray-400">
                            {{ deliveryFiltersActive
                                ? 'No attempts match these filters.'
                                : 'No delivery attempts recorded yet — this endpoint has not been sent an event.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
            <!-- The count is the server's, over the whole filtered history rather than the rows on
                 screen, so it does not shrink as you page through. -->
            <span class="text-xs text-gray-500">
                {{ deliveriesTotal }} {{ deliveriesTotal === 1 ? 'attempt' : 'attempts' }}
                <span *ngIf="deliveriesLastPage > 1">· page {{ deliveriesPage }} of {{ deliveriesLastPage }}</span>
            </span>
            <div class="flex items-center gap-2">
                <button type="button" class="rounded-full border px-4 py-2 text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-white/5"
                    [disabled]="deliveriesPage <= 1 || deliveriesLoading" (click)="goToDeliveriesPage(deliveriesPage - 1)">Previous</button>
                <button type="button" class="rounded-full border px-4 py-2 text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-white/5"
                    [disabled]="deliveriesPage >= deliveriesLastPage || deliveriesLoading" (click)="goToDeliveriesPage(deliveriesPage + 1)">Next</button>
                <button type="button" class="rounded-full border px-6 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="closeDeliveries()">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- ── ROTATE WEBHOOK SECRET ─────────────────────────────────────────────── -->
<div *ngIf="rotateSecretTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-[#0e1726]">

        <!-- After rotating: the new secret, once. -->
        <ng-container *ngIf="rotatedWebhookSecret; else rotateConfirmTpl">
            <h2 class="mb-1 text-base font-bold text-black dark:text-white">New Signing Secret</h2>
            <p class="mb-4 text-sm text-amber-600 dark:text-amber-400">
                Copy it now — it will <strong>never</strong> be shown again. The previous secret stopped working the moment it was rotated.
            </p>
            <div class="mb-4 flex items-center gap-2 rounded-xl bg-gray-100 p-3 dark:bg-white/10">
                <code class="min-w-0 flex-1 break-all font-mono text-xs">{{ rotatedWebhookSecret }}</code>
                <button type="button" class="shrink-0 rounded-lg border px-3 py-1 text-xs hover:bg-white dark:hover:bg-white/10" (click)="copyRotatedSecret()">
                    {{ rotatedSecretCopied ? 'Copied' : 'Copy' }}
                </button>
            </div>
            <p class="mb-4 text-xs text-gray-500">
                The client verifies the <code>X-Agentic-Signature</code> header with it:
                <code>sha256=HMAC-SHA256(secret, rawRequestBody)</code>, computed over the raw bytes.
            </p>
            <div class="flex justify-end">
                <button type="button" class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white" (click)="closeRotateSecret()">Done</button>
            </div>
        </ng-container>

        <ng-template #rotateConfirmTpl>
            <div class="mb-4 flex items-center justify-between">
                <h2 class="text-base font-bold text-black dark:text-white">Rotate signing secret</h2>
                <button type="button" class="text-gray-400 hover:text-gray-600" (click)="closeRotateSecret()">✕</button>
            </div>

            <p class="mb-3 text-sm text-gray-600 dark:text-gray-300">
                A new secret will be issued for
                <span class="font-mono text-xs">{{ rotateSecretTarget.url }}</span>.
                The endpoint, its subscription and its delivery history are kept.
            </p>
            <!-- Said plainly, because it is a client-visible outage and the operator is the only
                 person who can warn them before it starts. -->
            <p class="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                The old secret stops working immediately, so deliveries will fail until the client deploys the new one.
                Failed deliveries are retried for <strong>24 hours</strong> — at 5 minutes, 20 minutes, 1 hour, 6 hours
                and 24 hours after the first attempt — so nothing is lost if they deploy within a day.
            </p>
            <p *ngIf="rotateSecretError" class="mb-3 text-xs text-danger">{{ rotateSecretError }}</p>

            <div class="flex justify-end gap-3">
                <button type="button" class="rounded-full border px-6 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/5" (click)="closeRotateSecret()">Cancel</button>
                <button
                    type="button"
                    class="btn rounded-full bg-[#303030] px-6 py-2 text-sm text-white disabled:opacity-50"
                    [disabled]="rotateSecretLoading"
                    (click)="confirmRotateSecret()"
                >
                    {{ rotateSecretLoading ? 'Rotating…' : 'Rotate secret' }}
                </button>
            </div>
        </ng-template>
    </div>
</div>
